import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import contentService from '../../services/contentService';
import './ContentPagesPage.css';

function ContentPagesPage() {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const loadPages = async () => {
      try {
        const params = {};
        if (searchTerm) params.search = searchTerm;
        if (statusFilter) params.status = statusFilter;

        const response = await contentService.getPages(params);
        // Handle backend pagination format
        // Backend returns: { success: true, data: { content: [...], totalElements: N } }
        const pagesData = response.data?.data?.content || response.data?.content || response.data;
        // Ensure we always set an array
        setPages(Array.isArray(pagesData) ? pagesData : []);
      } catch (err) {
        toast.error('Failed to load pages');
        console.error('Error fetching pages:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPages();
  }, [searchTerm, statusFilter]);

  const fetchPages = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const response = await contentService.getPages(params);
      // Handle backend pagination format
      // Backend returns: { success: true, data: { content: [...], totalElements: N } }
      const pagesData = response.data?.data?.content || response.data?.content || response.data;
      // Ensure we always set an array
      setPages(Array.isArray(pagesData) ? pagesData : []);
    } catch (err) {
      toast.error('Failed to load pages');
      console.error('Error fetching pages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (page) => {
    const isCurrentlyPublished = page.status === 'PUBLISHED';

    try {
      // Use dedicated publish/unpublish endpoints
      if (isCurrentlyPublished) {
        await contentService.unpublishPage(page.id);
        toast.success('Page unpublished successfully');
      } else {
        await contentService.publishPage(page.id);
        toast.success('Page published successfully');
      }
      fetchPages();
    } catch (err) {
      toast.error('Failed to update page status');
      console.error('Error updating status:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    const map = {
      PUBLISHED: { label: 'Published', cls: 'published' },
      DRAFT:     { label: 'Draft',     cls: 'draft' },
      ARCHIVED:  { label: 'Archived',  cls: 'archived' },
    };
    const { label, cls } = map[status] || { label: status, cls: 'draft' };
    return <span className={`status-badge ${cls}`}>{label}</span>;
  };

  const publishedCount = pages.filter((p) => p.status === 'PUBLISHED').length;
  const draftCount = pages.filter((p) => p.status === 'DRAFT').length;

  if (isLoading) {
    return (
      <div className="content-pages">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading pages...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="content-pages">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Content Pages</h1>
          <p>Manage static pages for your website.</p>
        </div>
        <div className="page-header-actions">
        </div>
      </div>

      {/* Stats */}
      <div className="page-stats">
        <div className="stat-item">
          <span className="stat-value">{pages.length}</span>
          <span className="stat-label">Total Pages</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{publishedCount}</span>
          <span className="stat-label">Published</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{draftCount}</span>
          <span className="stat-label">Drafts</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="PUBLISHED">Published</option>
          <option value="DRAFT">Draft</option>
        </select>
      </div>

      {/* Pages List */}
      <div className="pages-list">
        {pages.length === 0 ? (
          <div className="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <h3>No pages found</h3>
            <p>No content pages match your filters.</p>
          </div>
        ) : (
          pages.map((page) => (
            <div key={page.id} className="page-card">
              <div className="page-card-main">
                <div className="page-info">
                  <div className="page-title-row">
                    <h3>
                      <Link to={`/content/pages/${page.id}/edit`}>{page.title}</Link>
                    </h3>
                    {getStatusBadge(page.status)}
                  </div>
                  <div className="page-meta">
                    <span className="page-slug">/{page.slug}</span>
                    <span className="meta-separator">•</span>
                    <span>Updated {formatDate(page.updatedAt)}</span>
                    <span className="meta-separator">•</span>
                    <span>by {page.author}</span>
                  </div>
                </div>
                <div className="page-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleToggleStatus(page)}
                    title={page.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                  >
                    {page.status === 'PUBLISHED' ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                  <Link
                    to={`/content/pages/${page.id}/edit`}
                    className="btn-icon"
                    title="Edit"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ContentPagesPage;
